import { PALETTE_MAGENTA } from '@shared/builtinPulseMaps/palettes';
import { buildBuiltinAudio } from '@shared/builtinPulseMaps/audioFromOnsets';
import audioOnsets from '@shared/circlesBuiltinMaps/audio/on-and-on.onsets.json' with { type: 'json' };
import { buildPianoChart } from '@shared/builtinPianoMaps/pianoFromOnsets';
import type { ManiaNote } from '@shared/maniaNote';
import type { PianoChart } from '@shared/pianoChart';

/**
 * Hand-crafted teaching chart for `/piano/tutorial`. Three sections,
 * each anchored to a beat range so the React-side overlay text can
 * advance in sync with the chart:
 *
 *   beats  4-12  basic taps (alternating lanes, single-finger reads)
 *   beats 13-20  hold notes (the new chevron-textured ribbon)
 *   beats 22-27  dual / chord tiles (two lanes at once)
 *   beats 28-30  short freeplay tail before the "you've got it" card
 *
 * Uses {@link buildPianoChart} so the Normal-variant timing windows
 * and approach ms match the regular catalogue — a player who clears
 * the tutorial steps into the easiest tier at the same reading speed
 * they just trained on.
 *
 * The chart deliberately does NOT route through the onset-driven
 * synthesiser — handcrafted beat positions on the bar give a
 * predictable rhythm that maps cleanly to teaching copy ("first the
 * floor, then the ceiling..."), with no risk that audio-onset jitter
 * shifts a tile by a quarter beat between sessions.
 */
const TUTORIAL_TOTAL_BEATS = 32;

export function buildTutorialPianoChart(): PianoChart {
  const audio = buildBuiltinAudio({
    src: '/audio/circles/on-and-on.mp3',
    attribution:
      'Tutorial uses "On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
    data: audioOnsets,
  });

  // Whole-beat placement on the slowest builtin track (87 BPM → ~690ms
  // per beat) so a brand-new player has nearly three quarters of a
  // second between each lesson element. Lanes alternate without ever
  // stacking same-lane jacks so muscle memory builds across the QWER
  // row from the first run.
  //
  // Audio metadata MUST match the regular On & On piano map
  // (`builtinPianoMaps/01-on-and-on.ts`): the Circles cut of the MP3
  // places beat 0 at file t=0, so it has to be paired with the Circles
  // onsets (offsetMs 0), not the Pulse onsets (offsetMs ~1741) whose
  // intro silence belongs to the longer `/audio/tracks` master. Pairing
  // the wrong onsets starts playback ~1.74s into the cut and desyncs
  // the backing track from the chart.
  const notes: ManiaNote[] = [
    // Section A — basic taps (overlay 1).
    { id: 't-01', beat: 4, lane: 1 },
    { id: 't-02', beat: 6, lane: 2 },
    { id: 't-03', beat: 8, lane: 0 },
    { id: 't-04', beat: 10, lane: 3 },

    // Section B — hold notes (overlay 2). Both holds are 2-beat
    // ribbons: long enough to feel like a sustain, short enough that
    // a slip is easy to recover from.
    { id: 'h-01', beat: 13, lane: 1, holdEndBeat: 15 },
    { id: 'h-02', beat: 17, lane: 2, holdEndBeat: 19 },

    // Section C — duals / chords (overlay 3). One symmetric inner
    // pair (W+E) then one outer pair (Q+R) so the player practises
    // both hand-spans.
    { id: 'd-01', beat: 22, lane: 1, dualPartnerLane: 2 },
    { id: 'd-02', beat: 25, lane: 0, dualPartnerLane: 3 },

    // Section D — freeplay tail (overlay 4). Two confidence-builder
    // taps before the "you're ready" card lands.
    { id: 't-05', beat: 28, lane: 2 },
    { id: 't-06', beat: 30, lane: 1 },
  ];

  return buildPianoChart({
    baseSlug: 'tutorial-piano',
    variant: 'normal',
    title: 'How to play (Piano)',
    description:
      'Quick teaching chart on a slow track. Tap the falling tiles when they reach the line.',
    bpm: audioOnsets.bpm,
    difficulty: 1,
    totalBeats: TUTORIAL_TOTAL_BEATS,
    palette: PALETTE_MAGENTA,
    audio,
    notes,
  });
}

/**
 * Overlay instructions for the tutorial page, each anchored to a beat
 * range. Renderer in PianoTutorialPage shows the most recent overlay
 * whose `fromBeat <= currentBeat < untilBeat`.
 */
export interface PianoTutorialOverlay {
  fromBeat: number;
  untilBeat: number;
  title: string;
  body: string;
}

/**
 * Returns the overlay sequence with input wording adapted to the
 * device. Touch devices get "tap on the lane" copy; keyboard devices
 * see both bindings (Piano's primary QWER row plus the DFJK alt) so
 * first-time players know either home-row position works.
 */
export function getPianoTutorialOverlays(isTouch: boolean): PianoTutorialOverlay[] {
  const tapVerb = isTouch ? 'Tap the lane' : 'Press Q W E R (or D F J K)';
  const holdVerb = isTouch ? 'Press and hold the lane' : 'Hold the key';
  return [
    {
      fromBeat: -8,
      untilBeat: 4,
      title: 'Tap the tiles when they reach the line',
      body: `${tapVerb} the moment a tile crosses the bottom bar. Four lanes, one key each.`,
    },
    {
      fromBeat: 4,
      untilBeat: 13,
      title: 'One tap per tile',
      body: 'A clean tile is a single tap. Catch each one as it lands on the line — the closer to centre, the cleaner the score.',
    },
    {
      fromBeat: 13,
      untilBeat: 22,
      title: 'Hold tiles have arrows',
      body: `${holdVerb} from the moment the head touches the line, then release the second the top of the ribbon meets it.`,
    },
    {
      fromBeat: 22,
      untilBeat: 28,
      title: 'Chord tiles light up two lanes',
      body: 'When two tiles arrive on the same beat, hit both keys together. Watch for the linked-pair marker on top.',
    },
    {
      fromBeat: 28,
      untilBeat: Number.POSITIVE_INFINITY,
      title: 'You\u2019ve got it',
      body: 'Free play to the end of the bar, then pick a real track from the home screen.',
    },
  ];
}
